import { Alert, ScrollArea, Skeleton, TreeList } from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ATTRIBUTE_TYPES,
  AttributeResponse,
  ROOT_STRUCT_CODE,
  useAllAttributeGroups,
  useAttributes,
} from '@/entities/attributes';
import { SimpleAttributesGroup } from './SimpleAttributesGroup';
import { StructureGroup } from './StructureGroup';

export type AttributesListProps = {
  showDeleted?: boolean;
  search?: string;
};

type ListType = {
  simpleAttributes: StructureGroupData;
  groupedStructures: StructureGroupData[];
};

type StructureGroupData = {
  structCode: string;
  structName: string;
  structureAttribute?: AttributeResponse; // Атрибут типа STRUCTURE с parentStructCode = SIMPLE
  attributes: AttributeResponse[]; // Атрибут внутри этой группы/структуры
};

/** Атрибут найден, если запрос встречается в его названии, коде или описании; регистр не учитывается */
function matchesSearch(attribute: AttributeResponse, search: string) {
  const query = search.toLowerCase();
  return (
    attribute.name.toLowerCase().includes(query) ||
    attribute.code.toLowerCase().includes(query) ||
    attribute.description.toLowerCase().includes(query)
  );
}

function filterAttributes(structureGroups: StructureGroupData[], params: { groupCode?: string; search?: string }) {
  const { groupCode } = params;
  // Код атрибута обычно вставляют из буфера — вместе с ним приходят пробелы, по которым не совпало бы ничего
  const search = params.search?.trim();

  return structureGroups.reduce<StructureGroupData[]>((acc, structureGroup) => {
    if (structureGroup.structCode === ROOT_STRUCT_CODE) {
      const filteredAttributes = structureGroup.attributes.filter((attr) => {
        const hasGroup = !groupCode || !!attr.attributeGroups?.some((ag) => ag.code === groupCode);
        return hasGroup && (!search || matchesSearch(attr, search));
      });
      if (filteredAttributes.length) {
        acc.push({ ...structureGroup, attributes: filteredAttributes });
      }
      return acc;
    }

    if (!structureGroup.structureAttribute) {
      return acc;
    }

    const structHasSomeGroup =
      !groupCode || !!structureGroup.structureAttribute.attributeGroups?.some((ag) => ag.code === groupCode);
    if (!structHasSomeGroup) {
      return acc;
    }

    if (!search) {
      acc.push(structureGroup);
      return acc;
    }

    // Совпавшая структура остаётся со всем содержимым — иначе её код нашёлся бы, а состав пропал
    if (matchesSearch(structureGroup.structureAttribute, search)) {
      acc.push(structureGroup);
      return acc;
    }

    const matchedAttributes = structureGroup.attributes.filter((attr) => matchesSearch(attr, search));
    if (matchedAttributes.length) {
      acc.push({ ...structureGroup, attributes: matchedAttributes });
    }
    return acc;
  }, []);
}

export const AttributesList: FC<AttributesListProps> = ({ showDeleted, search }) => {
  const { t } = useTranslation('attributes');
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get('activeTab') || undefined;

  // 1. Запрос на получение групп атрибутов — тот же набор, что у вкладок над списком: расходиться они не должны
  const { isLoading: isLoadingGroups } = useAllAttributeGroups();

  // Состояние для отслеживания раскрытых групп
  const [openGroups, setOpenGroups] = useState<string[]>([ROOT_STRUCT_CODE]);

  // 2. Запрос на получение ВСЕХ атрибутов
  const { data: attributesData, isLoading: isLoadingAttributes } = useAttributes(
    {
      'is-deleted': showDeleted ? null : false,
      limit: 1000,
    },
    true,
  );

  // 3. Группировка атрибутов
  const { simpleAttributes, groupedStructures } = useMemo<ListType>(() => {
    const simple: StructureGroupData = {
      structCode: ROOT_STRUCT_CODE,
      structName: t('simpleAttributes'),
      attributes: [],
    };
    if (!attributesData?.attributes) {
      return {
        simpleAttributes: simple,
        groupedStructures: [],
      };
    }

    const allAttributes = attributesData.attributes;

    // Структуры: parentStructCode = SIMPLE и dataType = STRUCTURE
    const structuresMap = new Map<string, StructureGroupData>();

    // Обрабатываем все атрибуты
    allAttributes.forEach((attr) => {
      const parentStructCode = attr.parentStructCode || ROOT_STRUCT_CODE;

      // Простые атрибуты (parentStructCode = SIMPLE и dataType !== STRUCTURE)
      if (parentStructCode === ROOT_STRUCT_CODE && attr.dataType !== ATTRIBUTE_TYPES.STRUCTURE) {
        simple.attributes.push(attr);
      }
      // Структурные атрибуты (parentStructCode = SIMPLE и dataType = STRUCTURE)
      else if (
        parentStructCode === ROOT_STRUCT_CODE &&
        attr.dataType === ATTRIBUTE_TYPES.STRUCTURE &&
        attr.structCode
      ) {
        const structCode = attr.structCode;
        if (!structuresMap.has(structCode)) {
          structuresMap.set(structCode, {
            structCode,
            structName: attr.name,
            structureAttribute: attr,
            attributes: [],
          });
        }
        structuresMap.get(structCode)!.structName = attr.name;
        structuresMap.get(structCode)!.structureAttribute = attr;
      } else if (parentStructCode !== ROOT_STRUCT_CODE) {
        if (!structuresMap.has(parentStructCode)) {
          structuresMap.set(parentStructCode, {
            structCode: parentStructCode,
            structName: '',
            attributes: [],
          });
        }
        structuresMap.get(parentStructCode)!.attributes!.push(attr);
      }
    });

    const groupedStructures = [...structuresMap.values()]
      .map((structure) => ({
        ...structure,
        attributes: structure.attributes.sort((a, b) => (a.name < b.name ? -1 : 0)),
      }))
      .sort((a, b) => (a.structName < b.structName ? -1 : 0));

    simple.attributes.sort((a, b) => (a.name < b.name ? -1 : 0));

    return {
      simpleAttributes: simple,
      groupedStructures,
    };
  }, [attributesData?.attributes, t]);

  const filteredGroups = useMemo(() => {
    return filterAttributes([simpleAttributes, ...groupedStructures], { groupCode: activeTab, search });
  }, [simpleAttributes, groupedStructures, activeTab, search]);

  const handleValueChange = (value: string[]) => {
    setOpenGroups(value);
  };

  const isLoading = isLoadingAttributes || isLoadingGroups;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 h-full">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (!filteredGroups.length) {
    return <Alert className="text-muted-foreground p-4" title={t('noAttributes')} />;
  }

  return (
    <ScrollArea orientation={'vertical'}>
      <div className="flex flex-col gap-5 overflow-hidden">
        <TreeList.Root value={openGroups} onValueChange={handleValueChange} className={'max-w-[300px]'}>
          {filteredGroups.map((group) =>
            group.structCode === ROOT_STRUCT_CODE ? (
              <SimpleAttributesGroup attributes={group.attributes} key={group.structCode} />
            ) : (
              group.structureAttribute && (
                <StructureGroup
                  key={group.structCode}
                  attributes={group.attributes}
                  structure={group.structureAttribute}
                />
              )
            ),
          )}
        </TreeList.Root>
      </div>
    </ScrollArea>
  );
};
